import { useCallback, useEffect, useState } from "react";
import { Wand2, PencilLine, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg } from "../../lib/api";
import { getP, money } from "../../lib/casePath";
import { Btn, Card, Input } from "../kit/Kit";
import { REVISION_GROUPS } from "./fields";

const fmtSug = (f, v) => (v === undefined ? "—" : f.type === "money" ? money(v) : typeof v === "boolean" ? (v ? "Sí" : "No") : String(v));

const CfdiChart = ({ mensual }) => {
  const max = Math.max(...mensual.map((m) => Math.max(m.emitidas_neto, m.recibidas_neto)), 1);
  return (
    <div data-testid="revision-cfdi-chart">
      <div className="flex h-44 items-end gap-1.5">
        {mensual.map((m) => (
          <div key={m.mes} className="flex flex-1 items-end gap-0.5" title={`${m.mes}: emitida ${money(m.emitidas_neto)} · recibida ${money(m.recibidas_neto)}`}>
            <div className="w-1/2 rounded-t-md bg-[#111]" style={{ height: `${(m.emitidas_neto / max) * 176}px` }} />
            <div className="w-1/2 rounded-t-md bg-[#E8643C]" style={{ height: `${(Math.max(m.recibidas_neto, 0) / max) * 176}px` }} />
          </div>
        ))}
      </div>
      <div className="mt-1 flex gap-1.5">{mensual.map((m) => <span key={m.mes} className="flex-1 text-center font-mono text-[9px] text-[#8C867A]">{m.mes.slice(2)}</span>)}</div>
      <div className="mt-3 flex gap-5 text-xs text-[#5C5850]"><span className="flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded bg-[#111]" />Emitida neta</span><span className="flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded bg-[#E8643C]" />Recibida neta</span></div>
    </div>
  );
};

export const StepRevision = ({ id, c, onChange, exp, setExp, ro }) => {
  const [rv, setRv] = useState(null);
  const load = useCallback(() => http.get(`/expedientes/${id}/review`).then((r) => setRv(r.data)), [id]);
  useEffect(() => { load(); }, [load]);
  const apply = async () => {
    try {
      const r = await http.post(`/expedientes/${id}/apply-extraction`);
      setExp(r.data.expediente);
      toast.success(`${r.data.aplicados.length} datos aplicados · ${r.data.omitidos_editados.length} editados conservados`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!rv) return null;
  const fuentes = { ...rv.fuentes, ...(exp.sources || {}) };
  const sev = { alta: "border-l-rose-500 bg-rose-50/60", media: "border-l-amber-500 bg-amber-50/60", baja: "border-l-slate-300 bg-[#FAF8F5]" };
  return (
    <>
      <Card title="Revisión de datos extraídos" subtitle="Confirma o corrige cada cifra; los cambios quedan marcados como editados manualmente y se reflejan en el memo."
        testid="step-revision" right={!ro && <Btn kind="accent" onClick={apply} data-testid="apply-extraction-button"><Wand2 size={15} /> Aplicar datos extraídos</Btn>}>
        <div className="space-y-8">
          {REVISION_GROUPS.map(([title, fields]) => (
            <div key={title}>
              <h4 className="mb-3 text-xs font-semibold uppercase tracking-widest text-[#8C867A]">{title}</h4>
              <div className="overflow-hidden rounded-xl border border-[#E2DDD5]">
                <table className="w-full text-sm">
                  <thead className="bg-[#FAF8F5] text-left text-xs text-[#8C867A]"><tr><th className="px-4 py-2.5 font-semibold">Concepto</th><th className="w-56 px-4 py-2.5 font-semibold">Valor confirmado</th><th className="px-4 py-2.5 font-semibold">Extraído</th><th className="px-4 py-2.5 font-semibold">Fuente</th></tr></thead>
                  <tbody>
                    {fields.map((f) => {
                      const ed = exp.edited.includes(f.k);
                      const sug = rv.sugeridos[f.k];
                      return (
                        <tr key={f.k} className="border-t border-[#F2F0EC] transition-colors hover:bg-[#FDF0EB]/40" data-testid={`revision-row-${f.k.replace(/\./g, "-")}`}>
                          <td className="px-4 py-2 font-medium">{f.l}</td>
                          <td className="px-4 py-2"><Input f={f} value={getP(c, f.k)} onChange={(v) => onChange(f.k, v)} /></td>
                          <td className="px-4 py-2 tabular-nums text-[#5C5850]">{fmtSug(f, sug)}</td>
                          <td className="px-4 py-2 text-xs text-[#8C867A]">
                            {fuentes[f.k] || "Captura manual"}
                            {ed && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-800" data-testid={`edited-badge-${f.k.replace(/\./g, "-")}`}><PencilLine size={11} />Editado manualmente</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </Card>
      <div className="grid gap-8 lg:grid-cols-12">
        <Card title="Facturación CFDI mensual" subtitle={rv.cfdi ? `${rv.cfdi.cfdi_validos} CFDI válidos · RFC ${rv.cfdi.rfc_expediente}` : "Sin ZIP CIEC procesado"} className="lg:col-span-7" testid="revision-cfdi">
          {rv.cfdi?.mensual?.length ? <CfdiChart mensual={rv.cfdi.mensual} /> : <p className="text-sm text-[#8C867A]">Sube el ZIP de CIEC en Documentos.</p>}
        </Card>
        <Card title={`Alertas (${rv.alertas.length})`} subtitle="Inconsistencias entre EEFF, balanza, CFDI y declaraciones." className="lg:col-span-5" testid="revision-alerts">
          <div className="max-h-[420px] space-y-3 overflow-auto pr-1">
            {rv.alertas.length === 0 && <p className="text-sm text-[#8C867A]">Sin alertas.</p>}
            {rv.alertas.map((a, i) => (
              <div key={`${a.codigo}-${i}`} className={`rounded-xl border-l-4 p-3.5 ${sev[a.severidad]}`} data-testid={`revision-alert-${i}`}>
                <p className="flex items-center gap-1.5 text-sm font-semibold"><AlertTriangle size={14} className="text-[#E8643C]" />{a.titulo}</p>
                <p className="mt-1 text-xs text-[#5C5850]">{a.descripcion}</p>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </>
  );
};
